import { useEffect } from 'react';
import { styled } from '@mui/material/styles';
import { useParams, useSearchParams } from 'react-router-dom';
import { Grid, Paper } from '@mui/material';
import { format } from 'date-fns';
import { usePDF } from '@react-pdf/renderer';

import { addToFavourites } from '../../redux/slices/favourites';
import { dispatch, useSelector } from '../../redux/store';
import { getArticle, getRelatedCo, resetRelatedCo, getArticleAdjacent } from '../../redux/slices/articles';
import Page from '../../components/Page';
import useLocales from '../../hooks/useLocales';
import HeroSectionWithText from '../../components/custom/Details/HeroSectionWithText';
import Breadcrumb from '../../components/custom/Breadcrumb/Breadcrumb';
import AboutWithDate from '../../components/custom/Details/AboutWithDate';
// import DetailText from '../../components/custom/Details/DetailText';
import DetailMedia from '../../components/custom/Details/DetailMedia';
import FolderCollection from '../../components/custom-one-use/FolderCollection';
import Quiz from '../../components/custom/Details/Quiz';
import DetailMap from '../../components/custom/Details/DetailMap';
import DetailAddress from '../../components/custom/Details/DetailAddress';
import DetailEmbededVideo from '../../components/custom/Details/DetailEmbededVideo';
import Loading from '../../components/custom/Loading/Loading';
import DetailHTML from '../../components/custom/Details/DetailHTML';
import DetailIframe from '../../components/custom/Details/DetailIframe';
import DetailHeading from '../../components/custom/Details/DetailHeading';
import DetailGalery from '../../components/custom/Details/DetailGalery';
import LinkedArticles from '../../components/custom/Details/LinkedArticles';
import CustomSlovakianaHelmet from '../../components/custom/CustomSlovakianaHelmet';
import ArticlesCO from '../../components/custom/Details/ArticlesCO';
import ArticlePrintDetail from './components/ArticlePrintDetail';

// {Html = 0,Text = 1,Image = 2,ImageGallery = 3,Map = 4,Video = 5,VideoGallery = 6,List = 8,LinkedArticles = 9,LinkedObjects = 10,Quiz = 11}

const RootStyle = styled('div')(({ theme }) => ({
  paddingTop: theme.spacing(8),
  backgroundColor: theme.palette.background.paper,
  [theme.breakpoints.up('lg')]: {
    paddingTop: theme.spacing(14),
  },
}));

export default function ArticlesDetail() {
  const { translate, currentLang } = useLocales();
  const { id } = useParams();
  const { currentArticle, relatedCo, isLoading, prev, next } = useSelector((state) => state.articles);
  const { favourites } = useSelector((state) => state.favourites);
  // const printRef = useRef(null);
  const [printInstance, updatePrintInstance] = usePDF({
    document: <ArticlePrintDetail article={currentArticle} related={relatedCo} />,
  });
  const [params] = useSearchParams();
  const url = params.get('url');
  const fromPage = params.get('fromPage');

  let path = window.location.pathname.substring(0, window.location.pathname.indexOf('/', 1));
  const pathCatalogue = window.location.pathname.includes('en/') ? `/en${translate('urls.co')}` : translate('urls.co');

  if (window.location.pathname.startsWith('/en')) {
    const firstIndex = window.location.pathname.indexOf('/');
    const secondIndex = window.location.pathname.indexOf('/', firstIndex + 1);
    const thirdIndex = window.location.pathname.indexOf('/', secondIndex + 1);

    if (thirdIndex !== -1) {
      path = window.location.pathname.substring(0, thirdIndex);
    }
  }
  useEffect(() => {
    dispatch(getArticle(id, currentLang.getValue));

    const query = JSON.parse(url || 'null');
    const index = query?.index || 0;
    const searchText = (query?.search || '').replaceAll('-', ' ');
    const sortAttribute = (query?.sort || 'displayDate').replaceAll('-', ' ');
    const sortDirection = (query?.sortDirection || '1').replaceAll('-', ' ');
    const filter = JSON.parse(query?.filter || '{"categoryKeys":[]}');
    const { categoryKeys, tagKeys } = filter;
    /* const categoryKeys = filter.category?.length > 0 ? filter.category.map((item) => item.param) : undefined;
    const tags = Object.entries(filter).map((item) => {
      if (item[0] !== 'category' && typeof item[1] === 'object') return item[1].map((i) => i.param);
      return [];
    });
    const tagKeys = [];
    tags.forEach((item) => tagKeys.push(...item)); */

    const articleTypes = path === '/en/articles' || path === `/clanky` ? [0, 1] : [1, 2];

    dispatch(
      getArticleAdjacent({
        articleTypes,
        index,
        searchText,
        sortAttribute,
        sortDirection,
        categoryKeys,
        tagKeys,
        searchByKeywords: query?.searchByKeywords || true,
        mapSearch: fromPage === 'map',
      })
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, url, path, currentLang.getValue, fromPage]);

  useEffect(() => {
    if (currentArticle?.folders?.length > 0) {
      dispatch(getRelatedCo(currentArticle.folders[0].folderKey));
    } else {
      dispatch(resetRelatedCo());
    }
  }, [id, currentArticle]);

  useEffect(() => {
    updatePrintInstance({ document: <ArticlePrintDetail article={currentArticle} related={relatedCo} /> });
    // ked tam dam aj "updatePrintInstance" tak to toci donekonecna
    // eslint-disable-next-line
  }, [currentArticle, relatedCo]);

  const heart = () => {
    dispatch(addToFavourites({ ...currentArticle, type: 'article' }));
  };
  const perexText = /<\/?[a-z][\s\S]*>/i.test(currentArticle.perex) ? (
    // eslint-disable-next-line react/no-danger
    <div dangerouslySetInnerHTML={{ __html: currentArticle.perex }} />
  ) : (
    currentArticle.perex
  );

  const meta = {
    description:
      (currentArticle?.seoDescription?.length > 1 ? currentArticle?.seoDescription : currentArticle?.perex) || '',
    keywords: currentArticle?.seoKeywords?.split(', ') || [],
    social: {
      ogTitle: `Slovakiana | ${currentArticle?.title}`,
      ogDescription: currentArticle?.perex || '',
      ogObjects: [
        {
          ogType: 'image',
          ogImageUrl:
            currentArticle.thumbnail !== undefined && currentArticle.thumbnail !== ''
              ? `${process.env.REACT_APP_API_URL}/image/download/${currentArticle?.thumbnail}?sizeIndex=0`
              : '',
          ogImageSecureUrl:
            currentArticle.thumbnail !== undefined && currentArticle.thumbnail !== ''
              ? `${process.env.REACT_APP_API_URL}/image/download/${currentArticle?.thumbnail}?sizeIndex=0`
              : '',
          ogImageAlt: currentArticle?.title || '',
        },
      ],
    },
  };

  return (
    <Page>
      <CustomSlovakianaHelmet
        title={`Slovakiana | ${translate('default.articles')} - ${currentArticle?.title}`}
        meta={meta}
      />
      <RootStyle>
        <Breadcrumb
          /* path={path} prevKey={prev} nextKey={next} urlParams={url} */ loading={isLoading}
          path={path}
          prevKey={prev}
          nextKey={next}
          urlParams={url}
        />
        {/* <Grid>
          // uz je hore v instancii
          <PDFViewer width="95%" height="800px">
            <ArticlePrintDetail
              article={currentArticle}
              related={relatedCo}
            />
          </PDFViewer>
        </Grid> */}
        <Grid sx={{ position: 'relative' }} /* ref={printRef} */>
          {isLoading && <Loading />}
          {currentArticle.key && (
            <>
              <HeroSectionWithText
                heartAction={() => heart()}
                isFavorite={favourites.findIndex((a) => a.key === currentArticle.key) !== -1}
                image={
                  currentArticle.thumbnail !== undefined && currentArticle.thumbnail !== ''
                    ? `${process.env.REACT_APP_API_URL}/image/download/${currentArticle.thumbnail}?sizeIndex=0`
                    : ''
                }
                text={currentArticle.title || ''}
                imgfit={'cover'}
                subtext={currentArticle.category || ''}
                thematic={currentArticle?.categories?.filter((item) => item.parentKey === '501')}
                favorite
              />
              <Paper sx={{ paddingLeft: 3, paddingRight: 3, margin: '0 auto' }}>
                <AboutWithDate
                  info={{
                    date:
                      format(
                        currentArticle.displayDate
                          ? new Date(currentArticle?.displayDate)
                          : new Date(currentArticle?.created),
                        'dd. MM. yyyy'
                      ) || '',
                    author: currentArticle.author || '',
                    time: currentArticle.estimatedReadTimeMinutes || '',
                  }}
                  text={perexText}
                  // thematic={currentArticle?.categories?.filter((item) => item.parentKey === '501')}
                  subtext={currentArticle.subtext || ''}
                  heartAction={() => heart()}
                  isFavorite={favourites.findIndex((a) => a.key === currentArticle.key) !== -1}
                  printInstance={printInstance}
                  // printRef={printRef}
                  title={currentArticle.title}
                />
              </Paper>
              {/* <Paper sx={{ maxWidth: '1100px', padding: 3, margin: '0 auto' }}> */}
              <Paper sx={{ padding: 3, margin: '0 auto' }}>
                {currentArticle.key &&
                  [...currentArticle?.components]
                    .sort((a, b) => a.order - b.order)
                    .map((section, idx) => (
                      <div key={idx}>
                        {section.type === 0 && <DetailHTML text={section.content?.htmlContent} />}
                        {section.type === 1 && <DetailIframe text={section.content?.text} />}
                        {section.type === 7 && (
                          <DetailHeading text={section.content?.text} type={section.content?.type} />
                        )}
                        {section.type === 2 && (
                          <DetailMedia media={section.content?.imageKey} description={section.content?.description} />
                        )}
                        {section.type === 3 && (
                          <DetailGalery media={section.content?.images} description={section.content?.description} />
                        )}
                        {section.type === 8 && <DetailAddress list={section.content.list || []} />}
                        {section.type === 5 && (
                          <DetailEmbededVideo
                            media={section.content?.videoUrl}
                            description={section.content?.description}
                          />
                        )}
                        {section.type === 11 && (
                          <Quiz title={section.content.description} data={section.content.questions} />
                        )}
                        {section.type === 12 && (
                          <ArticlesCO keyProp={section.content.culturalObjectKey} locale={currentLang.getValue} />
                        )}
                        {/* {section.type === 3 && (
                          <Carrousel
                            images={section.content.images || noDataFromBe(null, 'carousel', 'images', true)}
                            underText={section.content.description || noDataFromBe(null, 'carousel', 'text')}
                          />
                        )} */}

                        {/* {section.type === 4 && <DetailMap title={section.title || '***'} markers={section.markers || []} />} */}
                      </div>
                    ))}
              </Paper>
              {/* {currentArticle?.mapInfo && <Grid item>{map}</Grid>} */}
              {currentArticle?.mapInfo && (
                <DetailMap
                  markers={[
                    {
                      position: [currentArticle.mapInfo.lat, currentArticle.mapInfo.long],
                      title: currentArticle.title,
                      address: currentArticle?.mapInfo?.town?.includes('_')
                        ? currentArticle?.mapInfo?.town?.split('_')[1]
                        : currentArticle?.mapInfo?.town,
                    },
                  ]}
                  center={[currentArticle.mapInfo.lat, currentArticle.mapInfo.long]}
                  noscroll={false}
                  type={currentArticle.articleType === 1 ? 1 : 0}
                />
              )}
              {currentArticle?.links?.length > 0 && (
                <LinkedArticles title={translate('articles.related') || ''} data={currentArticle.links || []} />
              )}
              {relatedCo && (
                <FolderCollection
                  title={translate('articles.similarObjects') || ''}
                  folder={relatedCo || []}
                  redirect={pathCatalogue}
                  parent={currentArticle}
                />
              )}
            </>
          )}
        </Grid>
      </RootStyle>
    </Page>
  );
}
